// Data layer for the `alerts` schema. Triage transitions go through RPCs (audited,
// definer) rather than direct table writes (incident_triage has no client write policy).
import { alertsDb } from '@/lib/supabase';
import type {
  AlertSubscription, Developer, DeveloperAvailability, IncidentTriage, NotificationTarget, TriageStatus,
} from '@/types';

// ---- inbox / triage --------------------------------------------------------
export async function getMyInbox(statuses?: TriageStatus[]): Promise<IncidentTriage[]> {
  const { data, error } = await alertsDb().rpc('my_inbox', { p_statuses: statuses ?? null });
  if (error) throw error;
  return (data ?? []) as IncidentTriage[];
}

export async function getTriage(incidentId: string): Promise<IncidentTriage | null> {
  const { data, error } = await alertsDb().from('incident_triage').select('*').eq('incident_id', incidentId).maybeSingle();
  if (error) throw error;
  return data as IncidentTriage | null;
}

export async function claimIncident(incidentId: string, note?: string) {
  const { data, error } = await alertsDb().rpc('claim_incident', { p_incident_id: incidentId, p_note: note ?? null });
  if (error) throw error;
  return data as { ok: boolean; already_claimed_by?: string; claimed_by?: string };
}

export async function assignIncident(incidentId: string, assignee: string, note?: string) {
  const { data, error } = await alertsDb().rpc('assign_incident', { p_incident_id: incidentId, p_assignee: assignee, p_note: note ?? null });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function resolveIncident(incidentId: string, status: 'resolved' | 'wont_fix', note?: string) {
  const { data, error } = await alertsDb().rpc('resolve_incident', { p_incident_id: incidentId, p_status: status, p_note: note ?? null });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function setTriageNote(incidentId: string, note: string) {
  const { error } = await alertsDb().rpc('set_triage_note', { p_incident_id: incidentId, p_note: note });
  if (error) throw error;
}

// ---- developers / availability --------------------------------------------
export async function listDevelopers(): Promise<Developer[]> {
  const { data, error } = await alertsDb().from('developers').select('*').eq('is_active', true).order('display_name');
  if (error) throw error;
  return (data ?? []) as Developer[];
}

export async function listAvailability(): Promise<DeveloperAvailability[]> {
  const { data, error } = await alertsDb().from('developer_availability').select('*');
  if (error) throw error;
  return (data ?? []) as DeveloperAvailability[];
}

export async function getAvailability(developerId: string): Promise<DeveloperAvailability | null> {
  const { data, error } = await alertsDb().from('developer_availability').select('*').eq('developer_id', developerId).maybeSingle();
  if (error) throw error;
  return data as DeveloperAvailability | null;
}

export async function upsertAvailability(row: Partial<DeveloperAvailability> & { developer_id: string }) {
  const { error } = await alertsDb().from('developer_availability').upsert({ ...row, updated_at: new Date().toISOString() }, { onConflict: 'developer_id' });
  if (error) throw error;
}

// ---- subscriptions ---------------------------------------------------------
export async function getSubscriptions(developerId: string): Promise<AlertSubscription[]> {
  const { data, error } = await alertsDb().from('alert_subscriptions').select('*').eq('developer_id', developerId).order('created_at');
  if (error) throw error;
  return (data ?? []) as AlertSubscription[];
}

export async function upsertSubscription(row: Partial<AlertSubscription> & { developer_id: string }) {
  const payload = { ...row, updated_at: new Date().toISOString() };
  const { data, error } = await alertsDb().from('alert_subscriptions').upsert(payload).select().single();
  if (error) throw error;
  return data as AlertSubscription;
}

export async function deleteSubscription(id: string) {
  const { error } = await alertsDb().from('alert_subscriptions').delete().eq('id', id);
  if (error) throw error;
}

// ---- notification targets (push settings) ---------------------------------
export async function getMyTargets(developerId: string): Promise<NotificationTarget[]> {
  const { data, error } = await alertsDb().from('notification_targets').select('*').eq('developer_id', developerId).order('priority');
  if (error) throw error;
  return (data ?? []) as NotificationTarget[];
}

export async function savePushoverTarget(developerId: string, userKey: string, device?: string) {
  // one pushover target per dev in v1: delete existing, insert fresh
  await alertsDb().from('notification_targets').delete().eq('developer_id', developerId).eq('kind', 'pushover');
  const { error } = await alertsDb().from('notification_targets').insert({
    developer_id: developerId, kind: 'pushover',
    config: device ? { user_key: userKey, device } : { user_key: userKey },
    enabled: true, verified: false,
  });
  if (error) throw error;
}

export async function deleteTarget(id: string) {
  const { error } = await alertsDb().from('notification_targets').delete().eq('id', id);
  if (error) throw error;
}
